import React from 'react';
import {
  Toggle,
  Popover,
  Drawer,
  Menu,
  Button,
  Confirmation
} from 'freemium-ui';
import InfoIcon from 'images/info-red.svg';
import freshservice from 'images/freshservice.svg';
import incidentManagement from 'images/incident-management.svg';
import inventoryManagement from 'images/inventory-management.svg';
import serviceCatalog from 'images/service-catalog.svg';
import SlaManagement from 'images/sla-management-freshservice.svg';
import Loader from 'components/Loader';
import optionDot from 'images/option-dot.svg';

import CreateIntegration from './CreateIntegration';
import IntegrationForm from './IntegrationForm';
import {
  Card,
  CardHeader,
  CardList,
  Center,
  EmptyData,
  ErrorCard,
  IconButton,
  ModalContent
} from './styles';
import {
  useAddFsIntegration,
  useDeleteFsIntegration,
  useFsIntegration,
  useSaveFsIntegration
} from './hooks';
import { FormErrorCard } from './IntegrationForm/styles';

const options = [
  {
    id: 'edit',
    name: 'Edit'
  },
  {
    id: 'delete',
    name: 'Delete'
  }
];

const FreshServiceIntegration = props => {
  const {
    updateIntegrationCount = () => {},
    isIntegrationAllowed = true
  } = props;
  const [showAuthScreen, toggleAuthScreen] = React.useState(false);
  const [showDeletePopup, toggleDeletePopup] = React.useState(false);
  const [openDrawer, toggleDrawer] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [fsIntegration, setFsIntegration] = React.useState({});

  const {
    data: fsIntegrations = [],
    isError: isFsError,
    isLoading: isLoadingFsIntegrations
  } = useFsIntegration();
  const { mutate: addIntegration, isLoading: isAdding } = useAddFsIntegration({
    updateIntegrationCount
  });
  const {
    mutate: saveIntegration,
    isLoading: isSaving,
    variables: savePayload
  } = useSaveFsIntegration();
  const {
    mutate: deleteIntegration,
    isLoading: isDeleting
  } = useDeleteFsIntegration({ updateIntegrationCount });

  const onCreate = selectedIntegration => {
    setFsIntegration(selectedIntegration);
    toggleAuthScreen(false);
  };

  const handleMenuOption = (option, selectedIntegration) => {
    if (anchorEl) setAnchorEl(null);
    switch (option.id) {
      case 'edit':
        toggleDrawer(true);
        toggleAuthScreen(!selectedIntegration.is_valid);
        setFsIntegration(selectedIntegration);
        break;
      case 'save':
        saveIntegration({
          id: selectedIntegration.id,
          payload: {
            state: selectedIntegration.state === 'AC' ? 'DA' : 'AC'
          },
          mutate: true,
          statusChange: true
        });
        break;
      case 'delete':
        toggleDeletePopup(true);
        setFsIntegration(selectedIntegration);
        break;
      default:
    }
  };

  React.useEffect(() => {
    if (fsIntegrations.length > 0 && fsIntegration.id) {
      const newFsIntegration = fsIntegrations.find(
        i => i.id === fsIntegration.id
      );
      setFsIntegration(newFsIntegration);
    }
  }, [fsIntegrations]);

  return (
    <Card>
      <Confirmation
        isOpen={showDeletePopup && fsIntegration.id}
        onDismiss={() => {
          toggleDeletePopup(false);
        }}
        title="Delete Confirmation"
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        onConfirm={() => {
          deleteIntegration(fsIntegration.id);
          setFsIntegration({});
          toggleDrawer(false);
          toggleDeletePopup(false);
        }}
      >
        Are you sure you want to delete{' '}
        <strong>{fsIntegration.freshservice_domain}</strong> freshservice
        integration?
      </Confirmation>
      <CardHeader>
        <div>
          <img src={freshservice} alt="Freshservice Logo" />
          <span>Freshservice</span>
        </div>
        <div>
          <Button
            size="normal"
            onClick={() => {
              toggleAuthScreen(true);
              toggleDrawer(true);
              setFsIntegration({});
            }}
            disabled={
              !isIntegrationAllowed || isFsError || isLoadingFsIntegrations
            }
            className={
              !isIntegrationAllowed || isFsError || isLoadingFsIntegrations
                ? 'disabled'
                : ''
            }
            inline
            type="link"
          >
            &#43; Create Integration
          </Button>
        </div>
      </CardHeader>
      {!isLoadingFsIntegrations && fsIntegrations.length > 0 ? (
        <CardList>
          <colgroup>
            <col span="1" style={{ width: '80%' }} />
            <col span="1" style={{ width: '10%' }} />
            <col span="1" style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Domain</th>
              <th>Status</th>
              <th className="center">Action</th>
            </tr>
          </thead>
          <tbody>
            {fsIntegrations.map(integration => (
              <tr key={integration.id}>
                <td>
                  <div className="name">
                    <span>{integration.freshservice_domain}</span>
                    {!integration.is_valid && (
                      <ErrorCard>
                        <img src={InfoIcon} alt="info" />

                        <div>
                          {integration.errorMessage || 'Invalid configuration'}
                        </div>
                        <div
                          role="none"
                          className="config"
                          onClick={() =>
                            handleMenuOption({ id: 'edit' }, integration)
                          }
                        >
                          Edit
                        </div>
                      </ErrorCard>
                    )}
                    {integration.is_valid && integration.ticket_status && (
                      <FormErrorCard
                        color="#050505"
                        margin="unset"
                        background="#ffecf0"
                        borderColor="#ffd0d6"
                        className="error-card"
                      >
                        <img src={InfoIcon} alt="info" />
                        <div className="divider" />
                        <div>Unable to create last ticket,</div>
                        <div className="message">
                          {integration.ticket_status}
                        </div>
                      </FormErrorCard>
                    )}
                  </div>
                </td>
                <td>
                  <Toggle
                    on={integration.state === 'AC'}
                    hasIcon={false}
                    handleChange={() =>
                      handleMenuOption({ id: 'save' }, integration)
                    }
                    disabled={isSaving && savePayload.id === integration.id}
                  />
                </td>
                <td className="center">
                  <IconButton
                    role="button"
                    tabIndex={0}
                    onClick={e => setAnchorEl(e.currentTarget)}
                    onKeyDown={({ keyCode, currentTarget }) => {
                      if (keyCode === 13) setAnchorEl(currentTarget);
                    }}
                    aria-describedby={`${integration.id}`}
                  >
                    <img src={optionDot} alt="." />
                  </IconButton>
                  <Popover
                    id={`${integration.id}`}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'left'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <Menu
                      options={options}
                      onSelect={v => handleMenuOption(v, integration)}
                    />
                  </Popover>
                </td>
              </tr>
            ))}
          </tbody>
        </CardList>
      ) : (
        <EmptyData>
          {isLoadingFsIntegrations ? (
            <Center>
              <Loader />
            </Center>
          ) : (
            <>
              <p className="header-line">
                Manage incidents better on Freshservice.{' '}
                <a
                  href="https://freshworks.com/freshservice/it-service-desk/incident-management-software?utm_source=Freemium_Freshping&utm_medium=referral&utm_campaign=Freshping_Integration_Signup_Nudge"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              </p>
              <div className="features">
                <div className="feature-item">
                  <img src={incidentManagement} alt="team-inbox icon" />
                  <span>Incident management</span>
                </div>
                <div className="divider" />
                <div className="feature-item">
                  <img src={inventoryManagement} alt="sla-management icon" />
                  <span>Asset management</span>
                </div>
                <div className="divider" />
                <div className="feature-item">
                  <img src={SlaManagement} alt="collision-detection icon" />
                  <span>SLA Management</span>
                </div>
                <div className="divider" />
                <div className="feature-item">
                  <img src={serviceCatalog} alt="schedule-reports icon" />
                  <span>Service Catalog</span>
                </div>
              </div>
            </>
          )}
        </EmptyData>
      )}
      <Drawer
        isOpen={openDrawer}
        onDismiss={() => toggleDrawer(false)}
        isFullHeight
        size="medium"
        clickOutToClose={false}
        placement="right"
      >
        <ModalContent>
          {showAuthScreen ? (
            <CreateIntegration
              onClose={() => {
                toggleDrawer(false);
                setFsIntegration({});
              }}
              addIntegration={addIntegration}
              saveIntegration={saveIntegration}
              deleteIntegration={() => {
                handleMenuOption({ id: 'delete' }, fsIntegration);
              }}
              onCreate={onCreate}
              isAdding={isAdding}
              isSaving={isSaving}
              toggleAuthScreen={toggleAuthScreen}
              isDeleting={isDeleting}
              fsIntegration={fsIntegration}
            />
          ) : (
            <IntegrationForm
              fsIntegration={fsIntegration}
              onClose={() => {
                toggleDrawer(false);
                setFsIntegration({});
              }}
              toggleAuthScreen={toggleAuthScreen}
            />
          )}
        </ModalContent>
      </Drawer>
    </Card>
  );
};

export default FreshServiceIntegration;
